/* eslint-disable default-param-last */
/* eslint-disable no-case-declarations */
import { Reducer } from 'redux';
import produce from 'immer';

import tabId from 'services/tabId';
import moment from 'moment';
import { BiddingChatState, BiddingChatTypes, BiddingChatMessageTypes } from './types';

const DEFAULT_REQUEST_STATE = {
  loading: false,
  loaded: false,
  error: false,
};

const INITIAL_STATE: BiddingChatState = {
  bidding_chat: {
    current_chat: {},
    user_chats: {},
  },
  user_saved: {
    questions: [],
  },
  request: {
    fetch_chat: {},
    fetch_chat_messages: {},
    create_chat_message: {},
    update_chat_message: {},
    fetch_user_question: {},
    create_user_question: {},
    update_user_question: {},
    delete_user_question: {},
  },
};

// eslint-disable-next-line no-unused-vars
const reducer: Reducer<BiddingChatState> = (
  state = INITIAL_STATE,
  {
    type,
    result,
    biddingId,
    content,
    chatId,
    rating,
    messageId,
    questionId,
    questionContent,
    editalDownload,
    capagInfo,
  },
) => {
  // eslint-disable-next-line no-unused-vars
  return produce(state, (draft) => {
    switch (type) {
      case BiddingChatTypes.OPEN_CHAT: {
        const previousChat = state.bidding_chat.current_chat[tabId()];
        const shouldPreserveMetadata = previousChat?.currentBiddingId === biddingId;

        draft.bidding_chat.current_chat[tabId()] = {
          ...previousChat,
          currentBiddingId: biddingId,
          editalDownload:
            editalDownload ?? (shouldPreserveMetadata ? previousChat?.editalDownload : undefined),
          capagInfo: capagInfo ?? (shouldPreserveMetadata ? previousChat?.capagInfo : undefined),
          modalOpened: true,
        };
        break;
      }

      case BiddingChatTypes.CLOSE_CHAT:
        draft.bidding_chat.current_chat[tabId()] = {
          ...state.bidding_chat.current_chat[tabId()],
          modalOpened: false,
        };
        break;

      case BiddingChatTypes.FETCH_CHAT_REQUEST:
        draft.request.fetch_chat[tabId()] = { ...DEFAULT_REQUEST_STATE, loading: true };
        break;

      case BiddingChatTypes.FETCH_CHAT_SUCCESS:
        draft.bidding_chat.current_chat[tabId()] = {
          ...state.bidding_chat.current_chat[tabId()],
          currentChatId: result.data.id,
        };
        draft.request.fetch_chat[tabId()] = { ...DEFAULT_REQUEST_STATE, loaded: true };
        break;

      case BiddingChatTypes.FETCH_CHAT_FAILURE:
        draft.request.fetch_chat[tabId()] = { ...DEFAULT_REQUEST_STATE, error: true };
        break;

      case BiddingChatTypes.FETCH_CHAT_MESSAGES_REQUEST:
        draft.request.fetch_chat_messages[tabId()] = { ...DEFAULT_REQUEST_STATE, loading: true };
        break;

      case BiddingChatTypes.FETCH_CHAT_MESSAGES_SUCCESS:
        draft.bidding_chat.user_chats[tabId()] = {
          user_chat_messages: result.data.bidding_user_chat_messages,
        };

        draft.request.fetch_chat_messages[tabId()] = { ...DEFAULT_REQUEST_STATE, loaded: true };
        break;

      case BiddingChatTypes.FETCH_CHAT_MESSAGES_FAILURE:
        draft.request.fetch_chat_messages[tabId()] = { ...DEFAULT_REQUEST_STATE, error: true };
        break;

      case BiddingChatTypes.CREATE_CHAT_MESSAGE_REQUEST:
        const userMessage: BiddingChatMessageTypes = {
          id: 0,
          rating: null,
          content: content as string,
          created_at: moment(new Date()).format('YYYY-MM-DD HH:mm:ss'),
          updated_at: moment(new Date()).format('YYYY-MM-DD HH:mm:ss'),
          role: 'user',
          bidding_user_chat_id: chatId,
        };

        draft.bidding_chat.user_chats[tabId()].user_chat_messages = [
          ...state.bidding_chat.user_chats[tabId()].user_chat_messages,
          userMessage,
        ];
        draft.request.create_chat_message[tabId()] = { ...DEFAULT_REQUEST_STATE, loading: true };
        break;

      case BiddingChatTypes.CREATE_CHAT_MESSAGE_SUCCESS:
        draft.bidding_chat.user_chats[tabId()].user_chat_messages = [
          ...state.bidding_chat.user_chats[tabId()].user_chat_messages,
          result.data.bot_sent_message,
        ];
        draft.request.create_chat_message[tabId()] = { ...DEFAULT_REQUEST_STATE, loaded: true };
        break;

      case BiddingChatTypes.CREATE_CHAT_MESSAGE_FAILURE:
        draft.request.create_chat_message[tabId()] = { ...DEFAULT_REQUEST_STATE, error: true };
        break;

      case BiddingChatTypes.UPDATE_CHAT_MESSAGE_REQUEST:
        draft.request.update_chat_message[tabId()] = { ...DEFAULT_REQUEST_STATE, loading: true };
        break;

      case BiddingChatTypes.UPDATE_CHAT_MESSAGE_SUCCESS:
        const updatedMessageIndex = state.bidding_chat.user_chats[
          tabId()
        ].user_chat_messages?.findIndex((message) => message.id === messageId);

        if (updatedMessageIndex !== -1)
          draft.bidding_chat.user_chats[tabId()].user_chat_messages[updatedMessageIndex].rating =
            rating;

        draft.request.update_chat_message[tabId()] = { ...DEFAULT_REQUEST_STATE, loaded: true };
        break;

      case BiddingChatTypes.UPDATE_CHAT_MESSAGE_FAILURE:
        draft.request.update_chat_message[tabId()] = { ...DEFAULT_REQUEST_STATE, error: true };
        break;

      case BiddingChatTypes.CLEAR_CURRENT_CHAT:
        draft.bidding_chat.user_chats[tabId()] = {
          user_chat_messages: [],
        };
        break;
      case BiddingChatTypes.FETCH_USER_QUESTION_REQUEST:
        draft.request.fetch_user_question.request = { ...DEFAULT_REQUEST_STATE, loading: true };
        break;

      case BiddingChatTypes.FETCH_USER_QUESTION_SUCCESS:
        draft.user_saved.questions = result.data;
        draft.request.fetch_user_question.success = { ...DEFAULT_REQUEST_STATE, loaded: true };
        break;

      case BiddingChatTypes.FETCH_USER_QUESTION_FAILURE:
        draft.request.fetch_user_question.failure = { ...DEFAULT_REQUEST_STATE, error: true };
        break;

      case BiddingChatTypes.CREATE_USER_QUESTION_REQUEST:
        draft.request.create_user_question.request = { ...DEFAULT_REQUEST_STATE, loading: true };
        break;

      case BiddingChatTypes.CREATE_USER_QUESTION_SUCCESS:
        draft.user_saved.questions.push(result.data);
        draft.request.create_user_question.success = { ...DEFAULT_REQUEST_STATE, loaded: true };
        break;

      case BiddingChatTypes.CREATE_USER_QUESTION_FAILURE:
        draft.request.create_user_question.failure = { ...DEFAULT_REQUEST_STATE, error: true };
        break;

      case BiddingChatTypes.UPDATE_USER_QUESTION_REQUEST:
        draft.request.update_user_question.request = { ...DEFAULT_REQUEST_STATE, loading: true };
        break;

      case BiddingChatTypes.UPDATE_USER_QUESTION_SUCCESS:
        const questionIndex = draft.user_saved.questions.findIndex((q) => q.id === questionId);
        if (questionIndex !== -1) {
          draft.user_saved.questions[questionIndex].content = questionContent;
        }
        draft.request.update_user_question.success = { ...DEFAULT_REQUEST_STATE, loaded: true };
        break;

      case BiddingChatTypes.UPDATE_USER_QUESTION_FAILURE:
        draft.request.update_user_question.failure = { ...DEFAULT_REQUEST_STATE, error: true };
        break;

      case BiddingChatTypes.DELETE_USER_QUESTION_REQUEST:
        draft.request.delete_user_question.request = { ...DEFAULT_REQUEST_STATE, loading: true };
        break;

      case BiddingChatTypes.DELETE_USER_QUESTION_SUCCESS:
        draft.user_saved.questions = draft.user_saved.questions.filter((q) => q.id !== questionId);
        draft.request.delete_user_question.success = { ...DEFAULT_REQUEST_STATE, loaded: true };
        break;

      case BiddingChatTypes.DELETE_USER_QUESTION_FAILURE:
        draft.request.delete_user_question.failure = { ...DEFAULT_REQUEST_STATE, error: true };
        break;

      default:
        break;
    }
  });
};

export default reducer;
